import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../firebase/config';
import { 
    collection, 
    query, 
    doc, 
    orderBy, 
    onSnapshot, 
    deleteDoc
} from 'firebase/firestore';

// COMPONENTES Y ESTILOS
import ModalRefacturacion from './ModalRefacturacion';
import ModalNotificacion from './ModalNotificacion';
import ModalConfirmacion from './ModalConfirmacion';
import { getRefacturacionStatusChip, getPersonaChipClass } from '../../utils/styles';

// --- ICONOS ---
const IconEdit = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg>;
const IconTrash = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>;
const IconPlus = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path></svg>;

// --- HELPERS ---
const parseToNumeric = (value) => {
    if (typeof value === 'number') return value;
    if (!value) return 0;
    // Limpia puntos de miles y cambia coma por punto para decimales
    const clean = String(value).replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '');
    return parseFloat(clean) || 0;
};

const formatCurrency = (value, moneda = '$') => {
    const num = parseToNumeric(value);
    return new Intl.NumberFormat('es-AR', { 
        style: 'currency', 
        currency: moneda === 'USD' ? 'USD' : 'ARS', 
        minimumFractionDigits: 2 
    }).format(num);
};

const formatDate = (val) => {
    if (!val) return '-';
    if (val.seconds) return new Date(val.seconds * 1000).toLocaleDateString('es-AR');
    if (typeof val === 'string' && val.includes('-')) return val.split('-').reverse().join('/');
    return val;
};

const ModalDetalleRefacturacion = ({ show, onClose, poliza, client }) => {
    const [movimientos, setMovimientos] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Estados para sub-modales
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    const [deletionConfirm, setDeletionConfirm] = useState({ show: false, itemToDelete: null });

    const polizaId = poliza?.id || poliza?.polizaId;
    const clientId = poliza?.clienteId || client?.id;

    const nombreAsegurado = useMemo(() => {
        if (movimientos.length > 0) {
            const m = movimientos[0];
            const nombreM = `${m.clienteNombre || ''} ${m.clienteApellido || ''}`.trim();
            if (nombreM) return nombreM.toUpperCase();
        }
        const nombreP = `${poliza?.clienteNombre || ''} ${poliza?.clienteApellido || ''}`.trim();
        if (nombreP) return nombreP.toUpperCase();
        if (client) {
            if (client.persona === 'JURIDICA') return (client.nombre || 'S/D').toUpperCase();
            return `${client.nombre || ''} ${client.apellido || ''}`.trim().toUpperCase() || 'S/D';
        }
        return 'S/D';
    }, [movimientos, poliza, client]);

    useEffect(() => {
        if (!show || !polizaId || !clientId) return;

        setLoading(true);
        const ref = collection(db, `public/data/clientes/${clientId}/polizas/${polizaId}/endosos`);
        const q = query(ref, orderBy('numeroEndoso', 'desc'));

        const unsubscribe = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setMovimientos(data);
            setLoading(false);
        }, (err) => {
            console.error("Error cargando historial:", err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [show, polizaId, clientId]);

    // --- CÁLCULO DE DEUDA CORREGIDO ---
    const deudaTotals = useMemo(() => {
        // Los estados que NO deben sumar deuda son los cobrados o incobrables.
        // Las ANULACIONES se incluyen porque restan del total adeudado.
        const estadosFinalizadosSinDeuda = ['ABONADO', 'ABONADO - FALTA RECIBO', 'INCOBRABLE'];
        const totals = { ARS: 0, USD: 0 };
        
        movimientos.forEach((m) => {
            const status = (m.estadoRefacturacion || m.estadoGestionRenovacion || '').toUpperCase().trim();
            if (!estadosFinalizadosSinDeuda.includes(status)) {
                const isUSD = m.moneda === 'USD' || String(m.moneda).includes('U$S');
                const importe = parseToNumeric(m.primaEmitida);
                if (isUSD) {
                    totals.USD += importe;
                } else {
                    totals.ARS += importe;
                }
            }
        });
        return totals;
    }, [movimientos]);

    const handleDelete = async () => {
        if (!deletionConfirm.itemToDelete) return;
        const { id } = deletionConfirm.itemToDelete;
        setDeletionConfirm({ show: false, itemToDelete: null });
        try {
            await deleteDoc(doc(db, `public/data/clientes/${clientId}/polizas/${polizaId}/endosos`, id));
            setNotification({ show: true, message: 'Movimiento eliminado correctamente.', type: 'success' });
        } catch (e) {
            setNotification({ show: true, message: 'Error al eliminar el registro.', type: 'error' });
        }
    };

    if (!show) return null;

    return createPortal(
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[1000] flex items-center justify-center p-4 animate-fade-in text-left font-sans">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[98vw] flex flex-col overflow-hidden max-h-[95vh] border border-slate-200">
                
                {/* HEADER */}
                <div className="flex justify-between items-center px-8 py-5 border-b border-slate-100 bg-white shrink-0">
                    <div className="flex items-center gap-5">
                        <div className="flex flex-col text-left">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Visión Unificada</span>
                            <div className="flex items-center gap-2">
                                <h3 className="text-xl font-bold text-slate-800 tracking-tight uppercase">Historial de Refacturaciones Póliza #{poliza?.nPoliza || 'S/N'}</h3>
                                <span className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-lg border border-indigo-100 text-[10px] font-black uppercase tracking-tighter shadow-sm">{poliza?.compania || 'S/D'}</span>
                            </div>
                        </div>
                        <div className="h-10 w-px bg-slate-100 hidden md:block"></div>
                        <div className="flex flex-col hidden md:flex text-left">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Asegurado</span>
                            <p className="text-sm font-bold text-slate-600 uppercase truncate max-w-[300px]">{nombreAsegurado}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-all text-slate-300 hover:text-slate-600">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                <div className="overflow-hidden flex-grow flex flex-col p-6 bg-slate-50/20">
                    
                    {/* INDICADOR DE SALDO NETO */}
                    <div className="bg-white border border-slate-200 rounded-xl p-6 flex justify-between items-center mb-6 shadow-sm relative overflow-hidden shrink-0">
                        <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${ (deudaTotals.ARS + deudaTotals.USD) > 0 ? 'bg-red-500' : 'bg-emerald-500' }`}></div>
                        <div className="flex-grow text-left">
                            <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${ (deudaTotals.ARS + deudaTotals.USD) > 0 ? 'text-red-500' : 'text-emerald-600' }`}>
                                { (deudaTotals.ARS + deudaTotals.USD) >= 0 ? 'Refacturaciones Pendientes' : 'Saldo a Favor del Cliente' }
                            </p>
                            
                            <div className="flex flex-col gap-2">
                                {(deudaTotals.ARS !== 0) && (
                                    <div className="flex items-baseline gap-2 animate-fade-in">
                                        <span className={`text-3xl font-black tracking-tighter ${deudaTotals.ARS < 0 ? 'text-emerald-600' : 'text-slate-900'}`}>
                                            {formatCurrency(deudaTotals.ARS, '$')}
                                        </span>
                                        <span className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">ARS</span>
                                    </div>
                                )}

                                {deudaTotals.ARS !== 0 && deudaTotals.USD !== 0 && (
                                    <div className="w-full border-t border-slate-100 my-1"></div>
                                )}

                                {deudaTotals.USD !== 0 && (
                                    <div className="flex items-baseline gap-2 animate-fade-in">
                                        <span className={`text-3xl font-black tracking-tighter ${deudaTotals.USD < 0 ? 'text-emerald-600' : 'text-blue-600'}`}>
                                            {formatCurrency(deudaTotals.USD, 'USD')}
                                        </span>
                                        <span className="text-blue-400 font-bold uppercase text-[10px] tracking-widest">USD</span>
                                    </div>
                                )}

                                {deudaTotals.ARS === 0 && deudaTotals.USD === 0 && (
                                    <span className="text-xl font-bold text-slate-300 italic">Sin deuda pendiente</span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button 
                                onClick={() => { setEditingItem(null); setShowEditModal(true); }} 
                                className="bg-slate-900 hover:bg-black text-white px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center gap-2 shadow-lg uppercase tracking-widest active:scale-95"
                            >
                                <IconPlus /> Nueva Refacturación
                            </button>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm flex flex-col flex-grow">
                        <div className="overflow-x-auto overflow-y-auto custom-scrollbar h-full">
                            <table className="w-full text-left border-separate border-spacing-0 table-fixed min-w-[1700px]">
                                <thead className="bg-slate-900 text-white uppercase font-bold text-[9px] tracking-widest sticky top-0 z-20">
                                    <tr>
                                        <th className="p-4 w-28 text-center border-r border-slate-700">Inicio</th>
                                        <th className="p-4 w-28 text-center border-r border-slate-700">Fin</th>
                                        <th className="p-4 w-32 text-center border-r border-slate-700">Tipo</th>
                                        <th className="p-4 w-20 text-center border-r border-slate-700">Endoso</th>
                                        <th className="p-4 w-24 text-center font-mono border-r border-slate-700">Factura</th>
                                        <th className="p-4 w-36 text-right border-r border-slate-700">Importe</th>
                                        <th className="p-4 w-36 text-center border-r border-slate-700">Estado</th>
                                        <th className="p-4 w-36 text-center border-r border-slate-700">Responsable</th>
                                        <th className="p-4 w-32 text-center border-r border-slate-700">Reclamo</th>
                                        <th className="p-4 w-40 text-center border-r border-slate-700">Despacho</th>
                                        <th className="p-4 w-32 text-center border-r border-slate-700">¿Quién Cobró?</th>
                                        <th className="p-4 w-28 text-center border-r border-slate-700">F. Cobro</th>
                                        <th className="p-4 w-24 text-right pr-8 sticky right-0 bg-slate-900 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.5)]">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {loading ? (
                                        <tr><td colSpan="13" className="p-20 text-center text-slate-400 font-bold animate-pulse uppercase tracking-widest">Cargando movimientos...</td></tr>
                                    ) : movimientos.length === 0 ? (
                                        <tr><td colSpan="13" className="p-20 text-center text-slate-400 italic font-medium">Sin movimientos de refacturación.</td></tr>
                                    ) : movimientos.map((ref) => (
                                        <tr key={ref.id} className="hover:bg-blue-50/40 transition-colors group">
                                            <td className="p-4 text-center font-bold text-slate-700 border-r border-slate-100">{formatDate(ref.inicioVigencia)}</td>
                                            <td className="p-4 text-center font-bold text-slate-700 border-r border-slate-100">{formatDate(ref.finVigencia)}</td>
                                            
                                            <td className="p-4 text-center border-r border-slate-100">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                                                    ref.tipoEndoso === 'Anulación' ? 'bg-red-100 text-red-800 border-red-200' :
                                                    ref.tipoEndoso === 'Reducción' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                                                    ref.tipoEndoso === 'Aumento' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                                                    'bg-blue-100 text-blue-800 border-blue-200'
                                                }`}>
                                                    {ref.tipoEndoso || 'Refacturación'}
                                                </span>
                                            </td>

                                            <td className="p-4 text-center border-r border-slate-100">
                                                <span className="bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded font-black text-[10px] border border-indigo-100 uppercase">E-{ref.numeroEndoso || '0'}</span>
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-slate-500 uppercase border-r border-slate-100">{ref.numeroFactura || '-'}</td>
                                            <td className="p-4 text-right font-black text-sm border-r border-slate-100 tabular-nums">
                                                <span className={parseToNumeric(ref.primaEmitida) < 0 ? 'text-red-600' : 'text-blue-700'}>
                                                    {formatCurrency(ref.primaEmitida, ref.moneda)}
                                                </span>
                                            </td>
                                            
                                            <td className="p-4 text-center border-r border-slate-100">
                                                <span className={`${getRefacturacionStatusChip(ref.estadoRefacturacion || ref.estadoGestionRenovacion || 'Pendiente')} whitespace-nowrap`}>
                                                    {ref.estadoRefacturacion || ref.estadoGestionRenovacion || 'PENDIENTE'}
                                                </span>
                                            </td>
                                            
                                            <td className="p-4 text-center border-r border-slate-100">
                                                {ref.quienGestiono ? (
                                                    <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide inline-block ${getPersonaChipClass(ref.quienGestiono)}`}>{ref.quienGestiono}</div>
                                                ) : '-'}
                                            </td>

                                            <td className="p-4 text-center font-bold text-slate-400 tabular-nums border-r border-slate-100">{formatDate(ref.fechaReclamo)}</td>
                                            
                                            <td className="p-4 text-center border-r border-slate-100">
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="font-bold text-slate-600 uppercase text-[10px]">{ref.formaDespacho || '-'}</span>
                                                    {ref.fechaDespacho && <span className="text-[10px] text-slate-400 font-medium tabular-nums">{formatDate(ref.fechaDespacho)}</span>}
                                                </div>
                                            </td>
                                            
                                            <td className="p-4 text-center border-r border-slate-100">
                                                {ref.quienCobro ? (
                                                    <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide inline-block ${getPersonaChipClass(ref.quienCobro)}`}>{ref.quienCobro}</div>
                                                ) : '-'}
                                            </td>
                                            <td className="p-4 text-center text-slate-500 tabular-nums border-r border-slate-100">{formatDate(ref.fechaCobro)}</td>
                                            
                                            <td className="p-4 text-right pr-6 sticky right-0 z-10 bg-white group-hover:bg-[#f8faff] transition-colors shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.1)] border-l border-slate-100">
                                                <div className="flex justify-end gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => { setEditingItem(ref); setShowEditModal(true); }} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100 transition-all active:scale-90"><IconEdit /></button>
                                                    <button onClick={() => setDeletionConfirm({ show: true, itemToDelete: ref })} className="p-2 text-slate-300 hover:text-red-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-slate-100 transition-all active:scale-90"><IconTrash /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="px-8 py-5 border-t border-slate-100 bg-white shrink-0 flex justify-end">
                    <button onClick={onClose} className="px-10 py-3 bg-slate-900 hover:bg-black text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-lg active:scale-95">Cerrar Detalle</button>
                </div>
            </div>

            {/* MODALES HIJOS */}
            {showEditModal && (
                <ModalRefacturacion 
                    show={showEditModal} 
                    onClose={() => { setShowEditModal(false); setEditingItem(null); }} 
                    poliza={poliza} 
                    clientData={client} 
                    initialData={editingItem} 
                    onSuccess={(msg) => { setNotification({ show: true, message: msg, type: 'success' }); }} 
                />
            )}
            
            <ModalNotificacion show={notification.show} onClose={() => setNotification({ ...notification, show: false })} message={notification.message} type={notification.type} />
            <ModalConfirmacion show={deletionConfirm.show} onClose={() => setDeletionConfirm({ show: false, itemToDelete: null })} onConfirm={handleDelete} title="Eliminar Refacturación" message={`¿Estás seguro de eliminar este movimiento? Esta acción borrará el registro de forma permanente.`} />

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
                table { border-collapse: separate; }
                th.sticky { position: sticky; }
                .tabular-nums { font-variant-numeric: tabular-nums; }
            `}</style>
        </div>,
        document.body
    );
};

export default ModalDetalleRefacturacion;